import { cloneDeep } from 'lodash';
import { getPlainTextFromRawRTEState } from '@shoflo-rundown/draft-js-utils';
import { calculateDerivedFieldsForRows } from '@shoflo-rundown/cuesheet-data-helper';
import { createEditorStateFromRawRTEState } from 'shared/lib/rteHelper';

/**
 * Update Trackable CueClock State For A User When They Track
 *
 * @param userId
 * @param payload //trackable cue clock state
 * @param nextState
 */
export const cueClockMutatedRealTime = (userId, payload, nextState) => {
  const { trackableUsers } = nextState;

  if (trackableUsers[userId]) {
    let { trackableState } = trackableUsers[userId];

    if (!trackableState || !trackableState[payload.eventId]) {
      if (!trackableState) trackableState = {};
      trackableState[payload.eventId] = {};
    }

    trackableState[payload.eventId].cueClock = payload;
    nextState.trackableUsers[userId].trackableState = trackableState;
  }

  return nextState;
};

/**
 * Get Active Row Index from State
 *
 * Active row index represents the row currently visible
 * in the teleprompter view.
 *
 * @param {Object} nextState
 */
export const getActiveRowIndex = (nextState) => {
  const { rowOrder, rowId } = nextState;
  return rowOrder && rowId ? rowOrder.indexOf(rowId) : 0;
};

export const itemRowOrderUpdatedRealTimeTeleprompter = (
  itemSetId,
  newRowOrder,
  nextState,
) => {
  const { rowMap } = nextState;

  const activeRowIndex = getActiveRowIndex(nextState);

  const currentRowOrder = nextState.rowOrder;

  let targetRowIndex;
  let i = 0;

  while (i < currentRowOrder.length - 1 && targetRowIndex === undefined) {
    if (currentRowOrder[i] !== newRowOrder[i]) {
      targetRowIndex = i;
    }
    i++;
  }

  if (targetRowIndex < activeRowIndex || targetRowIndex === undefined) {
    const nextRowMap = nextState.cachedRowMap || nextState.rowMap;

    nextState.cachedRowOrder = newRowOrder;
    nextState.cachedRowMap = calculateDerivedFieldsForRowsWrapper(
      nextState,
      newRowOrder,
      nextRowMap,
    );
  } else {
    if (nextState.cachedRowOrder) nextState.cachedRowOrder = newRowOrder;
    if (nextState.cachedRowMap) {
      nextState.cachedRowMap = calculateDerivedFieldsForRowsWrapper(
        nextState,
        nextState.cachedRowOrder,
        nextState.cachedRowMap,
      );
    }

    nextState.rowOrder = newRowOrder;
    nextState.rowMap = calculateDerivedFieldsForRowsWrapper(
      nextState,
      nextState.rowOrder,
      rowMap,
    );
  }

  return nextState;
};

/**
 * Get Active Row Index from State
 *
 * Active row index represents the row currently visible
 * in the teleprompter view.
 *
 * @param {Object} nextState
 */
export const itemRowsDeletedRealTimeTeleprompter = (
  itemSetId,
  rowIds,
  nextState,
) => {
  const activeRowIndex = getActiveRowIndex(nextState);
  let targetRowIndex = 0;

  rowIds.forEach((rowId) => {
    const rowIndex = nextState.rowOrder.indexOf(rowId);
    if (rowIndex > targetRowIndex) targetRowIndex = rowIndex;
  });

  if (targetRowIndex < activeRowIndex) {
    const nextRowMap = nextState.cachedRowMap || nextState.rowMap;
    const nextRowOrder = nextState.cachedRowOrder || nextState.rowOrder;

    nextState.cachedRowOrder = deleteRowsFromRowOrder(
      itemSetId,
      rowIds,
      nextRowOrder,
    );
    nextState.cachedRowMap = calculateDerivedFieldsForRowsWrapper(
      nextState,
      nextState.cachedRowOrder,
      deleteRowsFromRowMap(itemSetId, rowIds, nextRowMap),
    );
  } else {
    if (nextState.cachedRowOrder)
      nextState.cachedRowOrder = deleteRowsFromRowOrder(
        itemSetId,
        rowIds,
        nextState.cachedRowOrder,
      );
    if (nextState.cachedRowMap) {
      nextState.cachedRowMap = calculateDerivedFieldsForRowsWrapper(
        nextState,
        nextState.cachedRowOrder,
        deleteRowsFromRowMap(itemSetId, rowIds, nextState.cachedRowMap),
      );
    }

    nextState.rowOrder = deleteRowsFromRowOrder(
      itemSetId,
      rowIds,
      nextState.rowOrder,
    );
    nextState.rowMap = calculateDerivedFieldsForRowsWrapper(
      nextState,
      nextState.rowOrder,
      deleteRowsFromRowMap(itemSetId, rowIds, nextState.rowMap),
    );
  }

  return nextState;
};

export const itemRowMultipleCellsMutatedRealTimeTeleprompter = (
  changes,
  nextState,
) => {
  const { itemSetId } = nextState;

  const activeRowIndex = getActiveRowIndex(nextState);
  let targetRowIndex = 0;

  const rowIds = Object.keys(changes);
  const changedRows = {};
  const cachedChangedRows = nextState.cachedChangedRows || {};

  rowIds.forEach((rowId) => {
    const rowIndex = nextState.rowOrder.indexOf(rowId);
    if (rowIndex > targetRowIndex) targetRowIndex = rowIndex;
    changedRows[rowIndex] = rowIndex;
  });

  if (targetRowIndex < activeRowIndex) {
    const nextRowMap = nextState.cachedRowMap || nextState.rowMap;
    nextState.cachedRowMap = applyMultipleChangesToRowMap(
      itemSetId,
      rowIds,
      changes,
      nextRowMap,
    );
    nextState.cachedChangedRows = Object.assign(cachedChangedRows, changedRows);
  } else {
    if (nextState.cachedRowMap) {
      nextState.cachedRowMap = applyMultipleChangesToRowMap(
        itemSetId,
        rowIds,
        changes,
        nextState.cachedRowMap,
      );
    }

    nextState.rowMap = applyMultipleChangesToRowMap(
      itemSetId,
      rowIds,
      changes,
      nextState.rowMap,
    );
    nextState.changedRows = changedRows;
  }

  return nextState;
};

/**
 * Add rows to state
 *
 * @param {Array} rows
 * @param {String} afterRowId
 * @param {Object} nextState
 */
export const itemRowsAddedRealTimeTeleprompter = (
  rows,
  afterRowId,
  nextState,
) => {
  const { itemSetId } = nextState;

  const activeRowIndex = getActiveRowIndex(nextState);
  const targetRowIndex = nextState.rowOrder.indexOf(afterRowId);

  if (targetRowIndex < activeRowIndex) {
    const nextRowMap = nextState.cachedRowMap || nextState.rowMap;
    const nextRowOrder = nextState.cachedRowOrder || nextState.rowOrder;

    nextState.cachedRowOrder = addRowsToRowOrder(
      rows,
      afterRowId,
      nextRowOrder,
    );
    nextState.cachedRowMap = calculateDerivedFieldsForRowsWrapper(
      nextState,
      nextState.cachedRowOrder,
      addRowsToRowMap(itemSetId, rows, nextRowMap),
    );
  } else {
    if (nextState.cachedRowOrder)
      nextState.cachedRowOrder = addRowsToRowOrder(
        rows,
        afterRowId,
        nextState.cachedRowOrder,
      );
    if (nextState.cachedRowMap && nextState.cachedRowOrder) {
      nextState.cachedRowMap = calculateDerivedFieldsForRowsWrapper(
        nextState,
        nextState.cachedRowOrder,
        addRowsToRowMap(itemSetId, rows, nextState.cachedRowMap),
      );
    }

    nextState.rowOrder = addRowsToRowOrder(
      rows,
      afterRowId,
      nextState.rowOrder,
    );
    nextState.rowMap = calculateDerivedFieldsForRowsWrapper(
      nextState,
      nextState.rowOrder,
      addRowsToRowMap(itemSetId, rows, nextState.rowMap),
    );
  }

  return nextState;
};

/**
 * Add rows to rowMap
 *
 * @param {Array} rows
 * @param {Object} rowMap
 */
export const addRowsToRowMap = (itemSetId, rows, rowMap) => {
  if (!rowMap[itemSetId]) return;

  rows.forEach((row) => {
    // clone the row to prevent editing the original row object
    const clonedRow = cloneDeep(row);

    clonedRow.item = clonedRow.item[0];
    const { rteTitleState } = clonedRow.item;
    if (rteTitleState) {
      clonedRow.item.editorTitleState =
        createEditorStateFromRawRTEState(rteTitleState);
    }
    let { cells } = clonedRow.item;
    if (cells) {
      Object.keys(cells).forEach((key) => {
        const cell = clonedRow.item.cells[key];
        if (cell && cell.rteState) {
          clonedRow.item.cells[key].editorState =
            createEditorStateFromRawRTEState(cell.rteState);
        } else {
          clonedRow.item.cells[key] = {};
        }
      });
    }

    rowMap[itemSetId].rows[clonedRow._id] = clonedRow;
  });

  return rowMap;
};

/**
 * Add rows to rowOrder
 *
 * @param {Array} rows
 * @param {String} afterRowId
 * @param {Array} rowOrder
 */
export const addRowsToRowOrder = (rows, afterRowId, rowOrder) => {
  const nextRowOrder = [...rowOrder];
  const newRowIds = rows.map((row) => row._id);

  const afterRowIndex = nextRowOrder.indexOf(afterRowId);
  nextRowOrder.splice(afterRowIndex + 1, 0, ...newRowIds);

  return nextRowOrder;
};

/**
 * Apply row cell changes to a rowMap object
 *
 * @param {String} itemSetId
 * @param {Array} rowIds
 * @param {Object} changes
 * @param {Object} rowMap
 */
export const applyMultipleChangesToRowMap = (
  itemSetId,
  rowIds,
  changes,
  rowMap,
) => {
  const nextRowMap = {
    [itemSetId]: { rows: Object.assign({}, rowMap[itemSetId].rows) },
  };

  rowIds.forEach((id) => {
    const rowChange = changes[id];

    if (rowMap[itemSetId].rows[id]) {
      const row = nextRowMap[itemSetId].rows[id];
      const newRow = Object.assign({}, row, { item: { ...row.item } });
      const keys = Object.keys(rowChange);

      keys.forEach((key) => {
        const change = rowChange[key];
        if (key === 'ItemNote' || key === 'ItemDescription') return;
        if (
          key === 'ItemStartTime' ||
          key === 'ItemPresetDuration' ||
          key === 'ItemDuration'
        ) {
          newRow.item = Object.assign(newRow.item, change);
        }
        if (key === 'ItemTitle') {
          newRow.item = Object.assign({}, newRow.item, {
            title: getPlainTextFromRawRTEState(change.rteState),
            editorTitleState: createEditorStateFromRawRTEState(change.rteState),
          });
        } else {
          newRow.item.cells = { ...newRow.item.cells };
          newRow.item.cells[key] = {
            editorState: createEditorStateFromRawRTEState(change.rteState),
            text: getPlainTextFromRawRTEState(change.rteState),
          };
        }
      });

      nextRowMap[itemSetId].rows[id] = newRow;
    }
  });

  return nextRowMap;
};

/**
 * Delete Rows from Row Map
 *
 * @param {String} itemSetId
 * @param {Array} rowIds
 * @param {Object} rowMap
 */
export const deleteRowsFromRowMap = (itemSetId, rowIds, rowMap) => {
  const nextRowMap = {
    [itemSetId]: { rows: Object.assign({}, rowMap[itemSetId].rows) },
  };

  rowIds.forEach((rowId) => {
    delete nextRowMap[itemSetId].rows[rowId];
  });

  return nextRowMap;
};

/**
 * Delete Rows from Row Order
 *
 * @param {String} itemSetId
 * @param {Array} rowIds
 * @param {Object} rowMap
 */
export const deleteRowsFromRowOrder = (itemSetId, rowIds, rowOrder) => {
  const nextRowOrder = [...rowOrder];

  rowIds.forEach((rowId) => {
    const rowIndex = nextRowOrder.indexOf(rowId);
    nextRowOrder.splice(rowIndex, 1);
  });

  return nextRowOrder;
};

/**
 * Check if new changes impact active telepropmter column
 * or the item title.
 *
 * @param {String} activeColumnId
 * @param {Object} changes
 */
export const hasValidTeleprompterColumnChanges = (activeColumnId, changes) => {
  const rowIds = Object.keys(changes);

  let validChangesFound = rowIds.find((rowId) => {
    const row = changes[rowId];
    return (
      row.hasOwnProperty('ItemTitle') || row.hasOwnProperty(activeColumnId)
    );
  });

  return !!validChangesFound;
};

export const calculateDerivedFieldsForRowsWrapper = (
  state,
  rowOrder,
  rowState,
) => {
  const { itemSetId, show } = state;
  const rowHash = calculateDerivedFieldsForRows(
    show,
    rowOrder,
    rowState,
    /**
     * Pure getter that gets the row from the state, given a rowId
     */
    (rowId, rowIndex, rowState) => {
      if (rowState[itemSetId].rows[rowId]) {
        return rowState[itemSetId].rows[rowId].item;
      }
      return null;
    },
    true,
  );

  rowOrder.forEach((rowId) => {
    if (rowState[itemSetId].rows[rowId]) {
      rowState[itemSetId].rows[rowId].item = Object.assign(
        rowState[itemSetId].rows[rowId].item,
        rowHash[rowId],
      );
    }
  });

  return rowState;
};

export default {
  cueClockMutatedRealTime,
  getActiveRowIndex,
  itemRowsDeletedRealTimeTeleprompter,
  deleteRowsFromRowMap,
  deleteRowsFromRowOrder,
  itemRowMultipleCellsMutatedRealTimeTeleprompter,
  itemRowOrderUpdatedRealTimeTeleprompter,
  applyMultipleChangesToRowMap,
  itemRowsAddedRealTimeTeleprompter,
  addRowsToRowOrder,
  addRowsToRowMap,
  hasValidTeleprompterColumnChanges,
  calculateDerivedFieldsForRowsWrapper,
};
